import { z } from 'zod';
import { ChipSchema } from './squad.ts';

/**
 * One gameweek's blank/double counts, for the squad's own fifteen only —
 * league-wide blanks and doubles are irrelevant to a chip decision unless the
 * manager actually owns players caught up in them.
 */
export const ChipCalendarEntry = z.object({
  gameweek: z.number().int(),
  /** How many of the squad's 15 have no fixture this gameweek. `null`, not a
   * computed 0, when nothing was ingested for this gameweek at all — no data
   * isn't "everyone's blanking", same reasoning as `meanDifficulty` below. */
  blanking: z.number().int().min(0).nullable(),
  /** How many of the squad's 15 have two or more fixtures this gameweek.
   * `null` under the same no-data condition as `blanking`. */
  doubling: z.number().int().min(0).nullable(),
  /** Mean FPL difficulty (1 easiest-5 hardest) across the squad's own
   * fixtures this gameweek — the signal that carries the calendar before
   * blanks and doubles exist. `null` when none of the squad's fixtures for
   * this gameweek are in the ingested data, not a computed 0: no data isn't
   * "easy". Optional so squads and tests built before this field existed
   * still satisfy the type. */
  meanDifficulty: z.number().nullable().optional(),
});
export type ChipCalendarEntry = z.infer<typeof ChipCalendarEntry>;

/**
 * One available chip's suggested gameweek. `gameweek` is `null` when nothing
 * in the horizon suits it — a chip is never forced onto a gameweek just to
 * have an answer, `reason` says why it's sitting out instead.
 */
export const ChipSuggestion = z.object({
  chip: ChipSchema,
  gameweek: z.number().int().nullable(),
  reason: z.string(),
});
export type ChipSuggestion = z.infer<typeof ChipSuggestion>;

/**
 * One chip left out of `suggestions` because the squad has already spent it,
 * and when. `active` is true only when this chip's gameweek is the current,
 * still-unfinished one — not merely the gameweek FPL's `active_chip` still
 * names, which it keeps reporting for days after that gameweek finishes.
 * Distinguishes "in play this gameweek" from "spent in an earlier one" —
 * different facts, worded differently by `ChipCalendar`.
 */
export const ChipExclusion = z.object({
  chip: ChipSchema,
  gameweek: z.number().int(),
  active: z.boolean(),
});
export type ChipExclusion = z.infer<typeof ChipExclusion>;

/** A season-shaped chip calendar: the blank/double counts across the horizon,
 * plus one suggestion per chip the squad still has available. */
export const ChipPlan = z.object({
  horizon: z.number().int(),
  entries: z.array(ChipCalendarEntry),
  suggestions: z.array(ChipSuggestion),
  /** Chips missing from `suggestions` because they're already spent, each
   * with the gameweek they were played in. `null`, not `[]`, when the
   * squad's chip history isn't known — no `entryId`, or a squad saved before
   * T-080 — so "known: none spent" isn't confused with "history untracked". */
  excludedChips: z.array(ChipExclusion).nullable(),
});
export type ChipPlan = z.infer<typeof ChipPlan>;

export const ChipPlanRequest = z.object({
  /** How many upcoming gameweeks the calendar should look ahead over. */
  horizon: z.number().int().min(1).max(8).default(6),
});
export type ChipPlanRequest = z.infer<typeof ChipPlanRequest>;
